import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "./Reveal";

const useLightboxLock = (open, onClose) => {
    useEffect(() => {
        if (!open) return;
        window.__lenis?.stop();
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            window.__lenis?.start();
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);
};

const LightboxCaption = ({ item }) => (
    <motion.figcaption
        className="mt-6 flex items-baseline justify-between gap-6 border-t border-[var(--line)] pt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
    >
        <div className="flex items-baseline gap-3 md:gap-5 min-w-0">
            <span className="font-mono2 text-[10px] md:text-xs text-[var(--red)]">{item.id}</span>
            <span className="font-display text-base md:text-2xl uppercase tracking-tight truncate" data-testid="lightbox-title">
                {item.title}
            </span>
        </div>
        <span className="font-mono2 text-[9px] md:text-[10px] tracking-[0.2em] text-[var(--mute)] uppercase whitespace-nowrap" data-testid="lightbox-meta">
            {item.meta}
        </span>
    </motion.figcaption>
);

export const Lightbox = ({ item, onClose }) => {
    useLightboxLock(Boolean(item), onClose);

    return (
        <AnimatePresence>
            {item && (
                <motion.div
                    key={item.id}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${item.title} — ${item.meta}`}
                    data-testid="lightbox"
                    data-cursor="CLOSE"
                    onClick={onClose}
                    className="fixed inset-0 z-[95] bg-[rgba(10,10,10,0.94)] backdrop-blur-md flex flex-col px-6 md:px-12 pt-24 pb-8 md:pb-10 cursor-pointer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, ease: EASE }}
                >
                    <motion.img
                        src={item.src}
                        alt={item.alt}
                        data-testid="lightbox-image"
                        className="flex-1 min-h-0 w-full object-contain select-none"
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.7, ease: EASE }}
                    />
                    <LightboxCaption item={item} />
                    <span className="absolute top-6 right-6 md:right-12 font-mono2 text-[10px] tracking-[0.25em] uppercase text-[var(--mute)]" data-testid="lightbox-close-hint">
                        Close ×
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
